import { themeVar } from '@/styles/theme';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import styled from 'styled-components';

import { PlusIcon } from '@/assets/icons/info-icons/plus';
import { RemoveIcon } from '@/assets/icons/info-icons/remove';
import Select from '@/components/basicComponents/select';
import { createSimpleSelectOption } from '@/components/basicComponents/select/utils';
import { useResponsive } from '../../../hooks/use-responsive';
import Button from '../../basicComponents/button';
import { AdditionalFilter, AdditionalFilters } from './types';
import { addSpaceBtwCamelCases, createNewFilter } from './utils';

const operators = ['is'];

interface MoreFiltersProps extends AdditionalFilters {
  onClose: () => void;
  setChosenFilters: Dispatch<SetStateAction<boolean>>;
}

const MoreFilters = ({
  onClose,
  setChosenFilters,
  applyFilters,
  filtersItems: filtersItemsWithoutInitialValues,
  initialValueTypeFilter = 'Select A Filter Option',
  filters: globalFilters
}: MoreFiltersProps) => {
  const { isPortrait, isMobile } = useResponsive();

  const [filters, setFilters] = useState<AdditionalFilter[]>(globalFilters);
  const filtersItems = useMemo(() => {
    return {
      [initialValueTypeFilter]: [createSimpleSelectOption('')],
      ...filtersItemsWithoutInitialValues
    };
  }, []);

  const filterTypes = useMemo(() => {
    return Object.keys(filtersItems).map((filter) => addSpaceBtwCamelCases(filter));
  }, []);

  const changeTypeFilter = (id: string, typeFilter: string) => {
    const cleanTypeFilter = typeFilter.replace(/\s/g, '');
    const newFilters = filters.map((filter) => {
      return filter.id === id && filtersItems[cleanTypeFilter]
        ? {
            ...filter,
            typeFilter: cleanTypeFilter,
            selected: filtersItems[cleanTypeFilter][0]?.value,
            listFilters: filtersItems[cleanTypeFilter]
          }
        : filter;
    });
    setFilters(newFilters);
  };

  const changeAdditionalFilters = (id: string, field: string, value: string) => {
    setFilters(filters.map((filter) => (filter.id === id ? { ...filter, [field]: value } : filter)));
  };

  const addMore = () => setFilters([...filters, createNewFilter(initialValueTypeFilter)]);

  const removeFilter = (id: string) => {
    const newFilters = filters.filter((filter) => filter.id !== id);
    setFilters(filters.length <= 1 ? [createNewFilter(initialValueTypeFilter)] : newFilters);
  };

  // for disable button and control add more button
  const isFilterSelected = () => {
    const lastElement = filters[filters.length - 1];
    if (!lastElement) return true;
    const isTypeFilterSelected = lastElement.typeFilter === initialValueTypeFilter;
    const isValueSelected = lastElement.selected === filtersItems[lastElement.typeFilter][0].value;
    return isTypeFilterSelected || isValueSelected;
  };
  const applySelectedFilters = () => {
    applyFilters(filters);
    onClose();
    setChosenFilters(true);
  };
  return (
    <MoreFilter>
      <MoreFilterTitle>Filters</MoreFilterTitle>
      <MoreFilterBody>
        <Divider />
        {filters.map(({ id, selected, operator, typeFilter, listOfItems }, index) => (
          <MoreFilterSelectorsWrap key={id}>
            <MoreFilterSelectors isPortrait={isPortrait} isMobile={isMobile}>
              <FilterSelect
                value={addSpaceBtwCamelCases(typeFilter)}
                setValue={(value) => changeTypeFilter(id, value)}
                options={filterTypes.map((filterType) => createSimpleSelectOption(filterType))}
                width={isPortrait && isMobile ? '100%' : '30%'}
                size="sm"
              />
              {initialValueTypeFilter !== typeFilter && (
                <>
                  <Select
                    value={operator}
                    setValue={(value) => changeAdditionalFilters(id, 'operator', value)}
                    options={operators.map((operator) => createSimpleSelectOption(operator))}
                    styleType={operators.length <= 1 ? 'disabled' : 'default'}
                    width={isPortrait && isMobile ? '100%' : '30%'}
                    size="sm"
                  />
                  <Select
                    value={selected}
                    setValue={(value) => changeAdditionalFilters(id, 'selected', value)}
                    options={filtersItems[typeFilter] || []}
                    width={isPortrait && isMobile ? '100%' : '30%'}
                    size="sm"
                    withSearch
                  />
                  <RemoveFilter onClick={() => removeFilter(id)}>
                    <RemoveIcon fill={themeVar('font.action')} />
                  </RemoveFilter>
                  {isPortrait && isMobile && <Divider />}
                </>
              )}
            </MoreFilterSelectors>
            {!isFilterSelected() && filters.length === index + 1 && (
              <MoreFilterAddWrap onClick={addMore}>
                <PlusIcon fill={themeVar('font.action')} />
                <MoreFilterAdd>Add more</MoreFilterAdd>
              </MoreFilterAddWrap>
            )}
          </MoreFilterSelectorsWrap>
        ))}
        <BtnWrap>
          <Button onClick={onClose} styleType="outline" size="md">
            Cancel
          </Button>
          <Button onClick={applySelectedFilters} styleType={isFilterSelected() ? 'disabled' : 'default'} size="md">
            Apply Filters
          </Button>
        </BtnWrap>
      </MoreFilterBody>
    </MoreFilter>
  );
};

export default MoreFilters;

const MoreFilter = styled.div`
  display: flex;
  flex-direction: column;
  align-self: stretch;
  padding: 3px 20px;
  width: 100%;
  background: ${themeVar('layer.base')};
  border-radius: 10px;
`;

const FilterSelect = styled(Select)`
  text-transform: capitalize;
`;

const MoreFilterSelectorsWrap = styled.div`
  display: flex;
  flex-direction: column;
`;

const MoreFilterSelectors = styled.div<{ isPortrait: boolean; isMobile: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: ${({ isPortrait, isMobile }) => (isPortrait && isMobile ? 'column' : 'row')};
  gap: 20px;
`;

const MoreFilterAddWrap = styled.div`
  width: fit-content;
  font-size: 18px;
  cursor: pointer;
  column-gap: 6px;
  display: flex;
  align-items: center;
  margin-top: 26px;
`;

const MoreFilterAdd = styled.div`
  font-family: 'Blinker';
  font-weight: 600;
  font-size: 13px;
  color: ${themeVar('font.action')};
`;

const MoreFilterBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const BtnWrap = styled.div`
  display: flex;
  gap: 10px;
  align-self: flex-end;
  padding-top: 20px;
  @media (max-width: 500px) {
    width: 100%;
    justify-content: space-between;
  }
`;

const RemoveFilter = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
`;

const Divider = styled.div`
  background: ${themeVar('border.base')};
  width: 100%;
  height: 1px;
`;

const MoreFilterTitle = styled.div`
  font-family: 'Blinker', serif;
  font-weight: 400;
  font-size: 19px;
  color: ${themeVar('font.base')};
`;
